import area from '@turf/area';
import { convertArea, convertLength, polygon } from '@turf/helpers';
import intersect from '@turf/intersect';
import { randomPoint } from '@turf/random';
import voronoi from '@turf/voronoi';
import {
  Cartesian2,
  Cartesian3,
  Cartographic,
  EllipsoidGeodesic,
  type PolylineGraphics,
  SceneTransforms,
  type Viewer,
} from 'cesium';
import { AreaMeasure, DistanceMeasure, Drawer, Measure } from 'cesium-extends';

import getText from '../lang/getText';

import type { Units } from '@turf/helpers';
import type {
  // Color,
  // Entity,
  // HeightReference,
  // LabelStyle,
  // NearFarScalar,
  PolygonGraphics,
} from 'cesium';
import type { DrawOption } from 'cesium-extends';
import type { Polygon } from 'geojson';

export type MeasureUnits = Units;

export type MeasureLocaleOptions = {
  start: string;
  total: string;
  area: string;

  formatLength(
    length: number,
    unitedLength: number,
    unit: MeasureUnits,
  ): string;

  formatArea(area: number, unitedArea: number, unit: MeasureUnits): string;
};

function getBounds(points: Cartesian2[]): number[] {
  const left = Math.min(...points.map((item) => item.x));
  const right = Math.max(...points.map((item) => item.x));
  const top = Math.max(...points.map((item) => item.y));
  const bottom = Math.min(...points.map((item) => item.y));

  const bounds = [left, top, right, bottom];
  return bounds;
}

// type MeasureOptions = {
//   labelStyle?: {
//     font?: string;
//     fillColor?: Color;
//     backgroundColor?: Color;
//     backgroundPadding?: Cartesian2;
//     outlineWidth?: number;
//     style?: LabelStyle;
//     pixelOffset?: Cartesian2;
//     scale?: number;
//     scaleByDistance?: NearFarScalar;
//     heightReference?: HeightReference;
//   };
//   /** defaults to kilometers */
//   units?: any;
//   onEnd?: (entity: Entity) => void;
//   ngOnMeasureComplete?: () => void;
//   drawerOptions?: Partial<DrawOption>;
//   /**
//    * @example
//    * {
//         start: '起点',
//         area: '面积',
//         total: '总计',
//         formatLength: (length, unitedLength) => {
//           if (length < 1000) {
//             return length + '米';
//           }
//           return unitedLength + '千米';
//         },
//         formatArea: (area, unitedArea) => {
//           if (area < 1000000) {
//             return area + '平方米';
//           }
//           return unitedArea + '平方千米';
//         }
//       }
//    */
//   locale?: Partial<MeasureLocaleOptions>;
// };

const gettext = getText.gettext;

export function mean(array: number[]): number {
  return (
    array.reduce((accumulator, currentValue) => accumulator + currentValue, 0) /
    array.length
  );
}
export class NgDrawer extends Drawer {
  ngOnDrawComplete: () => void;

  // TODO expand type
  constructor(viewer: Viewer, options: DrawOption) {
    super(viewer, options);
    //@ts-expect-error
    this.ngOnDrawComplete = options.ngOnDrawComplete;
  }

  pause(): void {
    super.pause();
    this.ngOnDrawComplete();
  }
}

// we need full alternative declaration because logic we need is in protected _updateLabelTexts
class NgDistanceMeasure extends Measure {
  ngDistance: number;
  ngOnComplete: (val: any) => void;

  constructor(viewer: Viewer, options: any) {
    super(viewer, options);
    this.ngDistance = 0;
    this.ngOnComplete = options.ngOnMeasureComplete;

    this.drawer = new NgDrawer(viewer, {
      ngOnDrawComplete: () => {
        const resultDistance =
          this.ngDistance > 1
            ? this.ngDistance
            : (1000 * this.ngDistance).toFixed(1);

        const resultUnit = this.ngDistance > 1 ? gettext('km') : 'm';

        const result = {
          type: 'distance',
          value: resultDistance,
          units: resultUnit,
        };
        this.ngOnComplete(result);
      },
      sameStyle: true,
      terrain: true,
      tips: {
        init: '',
        start: '',
        end: '',
      },
      ...options.drawerOptions,
    });

    this.drawer.mouseTooltip.destroy();
  }

  protected _updateLabelFunc(positions: Cartesian3[]) {
    this._labels.removeAll();
    positions.forEach((position) => {
      const newLabel = {
        position,
        ...this._labelStyle,
      };
      this._labels.add(newLabel);
    });
    this._updateLabelTexts(positions);
  }

  getDistance(start: Cartesian3, end: Cartesian3): number {
    return Cartesian3.distance(start, end);
  }

  protected _updateLabelTexts(positions: Cartesian3[]) {
    const num = positions.length;
    let distance = 0;
    for (let i = 0; i < num; i += 1) {
      const label = this._labels.get(i);
      if (i === 0) {
        label.text = this._locale.start;
        continue;
      } else {
        const newDis = +this.getDistance(
          positions[i - 1],
          positions[i],
        ).toFixed(2);
        // const unitedNewDis = +convertLength(
        //   newDis,
        //   'meters',
        //   //@ts-ignore
        //   this._units,
        // ).toFixed(2);

        distance += newDis;
        distance = +distance.toFixed(2);

        const unitedDistance = +convertLength(
          distance,
          'meters',
          //@ts-ignore
          this._units,
        ).toFixed(4);

        this.ngDistance = unitedDistance;

        //   label.text =
        //     (i === num - 1 ? `${this._locale.total}: ` : '') +
        //     this._locale.formatLength(distance, unitedDistance, this._units) +
        //     (i > 1
        //       ? `\n(+${this._locale.formatLength(
        //           newDis,
        //           unitedNewDis,
        //           this._units,
        //         )})`
        //       : '');
      }
    }
  }

  start(style: PolylineGraphics.ConstructorOptions = {}) {
    this._start('POLYLINE', {
      style,
      clampToGround: false,
    });
  }
}

export const getNgDistanceMeasure = (viewer: Viewer, options: any) => {
  const m = new NgDistanceMeasure(viewer, {
    ngOnMeasureComplete: options.ngOnMeasureComplete,
    units: 'kilometers',
    locale: {
      start: '',
      area: '',
      total: '',
      formatLength: (length: number, unitedLength: number) => {
        if (length < 1000) {
          return length + ' m';
        }
        return unitedLength + ' km';
      },
    },
    // drawerOptions: {
    //   tips: {
    //     init: 'Click to start drawing',
    //     start: 'double click to finish',
    //     end: '',
    //   },
    // },
  });

  return m;
};

class NgAreaMeasure extends Measure {
  ngArea: number;
  ngOnComplete: (val: any) => void;

  constructor(viewer: Viewer, options: any) {
    super(viewer, options);
    this.ngArea = 0;
    this.ngOnComplete = options.ngOnMeasureComplete;

    this.drawer = new NgDrawer(viewer, {
      ngOnDrawComplete: () => {
        const resultArea =
          this.ngArea > 1
            ? this.ngArea.toFixed(2)
            : +(1000000 * this.ngArea).toFixed(4);

        const resultUnit = this.ngArea > 1 ? 'km²' : 'm²';

        const result = {
          type: 'area',
          value: resultArea,
          units: resultUnit,
        };
        this.ngOnComplete(result);
      },
      sameStyle: true,
      terrain: true,
      tips: {
        init: '',
        start: '',
        end: '',
      },
      ...options.drawerOptions,
    });

    this.drawer.mouseTooltip.destroy();
  }

  protected _updateLabelFunc(positions: Cartesian3[]) {
    this._labels.removeAll();
    if (positions.length < 3) return;
    const position = new Cartesian3(
      ...['x', 'y', 'z'].map((key) =>
        mean(positions.map((item) => (item as any)[key])),
      ),
    );
    this._labels.add({
      position,
      ...this._labelStyle,
      pixelOffset: new Cartesian2(-100, 0),
    });
    this._updateLabelTexts(positions);
  }

  getArea(positions: Cartesian3[]): number {
    const lonlats = this._cartesian2Lonlat(positions);
    const pg = polygon([[...lonlats, lonlats[0]]]);
    const polygonArea = area(pg);
    return polygonArea;
  }

  protected _updateLabelTexts(positions: Cartesian3[]) {
    // const label = this._labels.get(0);
    const area = +this.getArea(positions).toFixed(6);
    if (this._units !== 'radians' && this._units !== 'degrees') {
      const unitedArea = +convertArea(area, 'meters', this._units).toFixed(6);

      this.ngArea = unitedArea;
    }
    // label.text = `${this._locale.area}: ${this._locale.formatArea(
    //   area,
    //   unitedArea,
    //   this._units,
    // )}`;
  }

  protected _getDistance(pos1: Cartesian3, pos2: Cartesian3): number {
    return Cartesian3.distance(pos1, pos2);
  }

  start(style: PolygonGraphics.ConstructorOptions = {}) {
    this.end();
    this._start('POLYGON', {
      style,
    });
  }
}

export const getNgAreaMeasure = (viewer: Viewer, options: any) => {
  const m = new NgAreaMeasure(viewer, {
    ngOnMeasureComplete: options.ngOnMeasureComplete,
    units: 'kilometers',
    locale: {
      start: '',
      area: '',
      total: '',
      formatArea: (area: number, unitedArea: number) => {
        if (area < 1000000) {
          return area + 'm²';
        }
        return unitedArea + 'km²';
      },
    },
  });

  return m;
};

class NgDistanceSurfaceMeasure extends DistanceMeasure {
  private _splitNum: number;

  ngDistance: number = 0;
  ngOnComplete: (val: any) => void;

  constructor(
    viewer: Viewer,
    options: any & {
      splitNum?: number;
    } = {},
  ) {
    super(viewer, options as any);
    this._splitNum = options.splitNum ?? 10;
    this.ngOnComplete = options.ngOnMeasureComplete;
    this._labels.removeAll();

    this.drawer = new NgDrawer(viewer, {
      ngOnDrawComplete: () => {
        const resultDistance =
          this.ngDistance > 1000
            ? (this.ngDistance / 1000).toFixed(4)
            : this.ngDistance.toFixed(1);

        const resultUnit = this.ngDistance > 1000 ? gettext('km') : 'm';

        const result = {
          type: 'distance',
          value: resultDistance,
          units: resultUnit,
        };
        this.ngOnComplete(result);
      },
      sameStyle: true,
      terrain: true,
      tips: {
        init: '',
        start: '',
        end: '',
      },
      ...options.drawerOptions,
    });

    this.drawer.mouseTooltip.destroy();
  }

  _updateLabelTexts(positions: string | any[]) {
    const num = positions.length;
    let distance = 0;
    for (let i = 0; i < num; i += 1) {
      const label = this._labels.get(i);
      if (i === 0) {
        label.text = this._locale.start;
        continue;
      } else {
        const newDis = +this.getDistance(
          positions[i - 1],
          positions[i],
        ).toFixed(2);

        distance += newDis;
        distance = +distance.toFixed(2);

        this.ngDistance = distance;
      }
    }
  }

  private _calculateSurfaceDistance(
    startPoint: Cartesian2,
    endPoint: Cartesian2,
  ): number {
    let resultDistance = 0;
    const sampleWindowPoints = [startPoint];
    const interval =
      Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) + (endPoint.y - startPoint.y, 2),
      ) / this._splitNum;

    for (let ii = 1; ii <= this._splitNum; ii += 1) {
      const tempPositon = this._findWindowPositionByPixelInterval(
        startPoint,
        endPoint,
        ii * interval,
      );
      sampleWindowPoints.push(tempPositon);
    }

    sampleWindowPoints.push(endPoint);
    for (let jj = 0; jj < sampleWindowPoints.length - 1; jj += 1) {
      resultDistance += this._calculateDetailSurfaceLength(
        sampleWindowPoints[jj + 1],
        sampleWindowPoints[jj],
      );
    }

    return resultDistance;
  }

  private _calculateDetailSurfaceLength(
    startPoint: Cartesian2,
    endPoint: Cartesian2,
  ): number {
    let innerS = 0;
    const surfaceStartCartesian3 = pickCartesian3(this._viewer, startPoint);
    const surfaceEndCartesian3 = pickCartesian3(this._viewer, endPoint);
    if (surfaceStartCartesian3 && surfaceEndCartesian3) {
      const cartographicStart = Cartographic.fromCartesian(
        surfaceStartCartesian3,
      );
      const cartographicEnd = Cartographic.fromCartesian(surfaceEndCartesian3);
      const geoD = new EllipsoidGeodesic();
      geoD.setEndPoints(cartographicStart, cartographicEnd);
      innerS = geoD.surfaceDistance;
      innerS = Math.sqrt(
        Math.pow(innerS, 2) +
          Math.pow(cartographicStart.height - cartographicEnd.height, 2),
      );
    }
    return innerS;
  }

  private _findWindowPositionByPixelInterval(
    startPosition: Cartesian2,
    endPosition: Cartesian2,
    interval: number,
  ): Cartesian2 {
    const result = new Cartesian2(0, 0);
    const length = Math.sqrt(
      Math.pow(endPosition.x - startPosition.x, 2) +
        Math.pow(endPosition.y - startPosition.y, 2),
    );
    if (length < interval) {
      return result;
    } else {
      const x =
        (interval / length) * (endPosition.x - startPosition.x) +
        startPosition.x;
      //alert(interval/length)
      const y =
        (interval / length) * (endPosition.y - startPosition.y) +
        startPosition.y;
      result.x = x;
      result.y = y;
    }
    return result;
  }

  getDistance(pos1: Cartesian3, pos2: Cartesian3): number {
    const start = SceneTransforms.worldToWindowCoordinates(
      this._viewer.scene,
      pos1,
    )!;
    const end = SceneTransforms.worldToWindowCoordinates(
      this._viewer.scene,
      pos2,
    )!;

    return this._calculateSurfaceDistance(start, end);
  }

  start(style: PolylineGraphics.ConstructorOptions = {}) {
    this._start('POLYLINE', {
      clampToGround: true,
      style,
    });
  }
}

function pickCartesian3(
  viewer: Viewer,
  position: Cartesian2,
): Cartesian3 | undefined {
  // We use `viewer.scene.pickPosition` here instead of `viewer.camera.pickEllipsoid` so that
  // we get the correct point when mousing over terrain.
  const ray = viewer.camera.getPickRay(position);
  if (ray) return viewer.scene.globe.pick(ray, viewer.scene);
  return undefined;
}

export const getNgSurfaceDistanceMeasure = (viewer: Viewer, options: any) => {
  const m = new NgDistanceSurfaceMeasure(viewer, {
    splitNum: 10, // number of splits of surface align line, big numbers affect performance severely
    ngOnMeasureComplete: options.ngOnMeasureComplete,
    units: 'kilometers',
    locale: {
      start: '',
      area: '',
      total: '',
      formatLength: (length: number, unitedLength: number) => {
        if (length < 1000) {
          return length + ' m';
        }
        return unitedLength + ' km';
      },
    },
  });

  return m;
};

class NgAreaSurfaceMeasure extends AreaMeasure {
  private _splitNum: number;

  ngArea: number;
  ngOnComplete: (val: any) => void;

  constructor(
    viewer: Viewer,
    options: any & {
      splitNum?: number;
    } = {},
  ) {
    super(viewer, options);
    this._splitNum = options.splitNum ?? 10;

    this.ngArea = 0;
    this.ngOnComplete = options.ngOnMeasureComplete;

    this.drawer = new NgDrawer(viewer, {
      ngOnDrawComplete: () => {
        const resultArea =
          this.ngArea > 1000000
            ? (this.ngArea / 1000000).toFixed(4)
            : +this.ngArea.toFixed(2);

        const resultUnit = this.ngArea > 1000000 ? 'km²' : 'm²';

        const result = {
          type: 'area',
          value: resultArea,
          units: resultUnit,
        };
        this.ngOnComplete(result);
      },
      sameStyle: true,
      terrain: true,
      tips: {
        init: '',
        start: '',
        end: '',
      },
      ...options.drawerOptions,
    });

    this.drawer.mouseTooltip.destroy();
  }

  private _calculateSurfaceArea(positions: Cartesian2[]): number {
    let result = 0;
    const bounds = getBounds(positions);
    const points = randomPoint(this._splitNum, {
      bbox: [bounds[0], bounds[1], bounds[2], bounds[3]],
    });
    const mainPoly = this._Cartesian2turfPolygon(positions);
    const voronoiPolygons = voronoi(points, {
      bbox: [bounds[0], bounds[1], bounds[2], bounds[3]],
    });
    voronoiPolygons.features.forEach((element) => {
      const intersectPoints = this._intersect(mainPoly, element.geometry);
      console.log('intersectPoints', intersectPoints.length);

      if (intersectPoints.length === 1) return;

      result += this.calculateDetailSurfaceArea(intersectPoints);
    });

    return result;
  }

  private calculateDetailSurfaceArea(positions: Cartesian2[]): number {
    const worldPositions: Cartesian3[] = [];

    positions.forEach((element) => {
      const pickResult = pickCartesian3(this._viewer, element);
      if (pickResult) worldPositions.push(pickResult);
    });
    const area = this._getWorldPositionsArea(worldPositions);

    return area;
  }

  _updateLabelTexts(positions: any) {
    const area2 = +this.getArea(positions).toFixed(4);
    if (this._units !== 'radians' && this._units !== 'degrees') {
      this.ngArea = area2;
    }
  }

  private _getWorldPositionsArea(positions: Cartesian3[]) {
    const x: number[] = [0];
    const y: number[] = [0];
    const geodesic = new EllipsoidGeodesic();
    const radiansPerDegree = Math.PI / 180.0; //角度转化为弧度(rad)
    //数组x,y分别按顺序存储各点的横、纵坐标值
    for (let i = 0; i < positions.length - 1; i += 1) {
      const p1 = positions[i];
      const p2 = positions[i + 1];
      const point1cartographic = Cartographic.fromCartesian(p1);
      const point2cartographic = Cartographic.fromCartesian(p2);
      geodesic.setEndPoints(point1cartographic, point2cartographic);
      const s = Math.sqrt(
        Math.pow(geodesic.surfaceDistance, 2) +
          Math.pow(point2cartographic.height - point1cartographic.height, 2),
      );
      const lat1 = point2cartographic.latitude * radiansPerDegree;
      const lon1 = point2cartographic.longitude * radiansPerDegree;
      const lat2 = point1cartographic.latitude * radiansPerDegree;
      const lon2 = point1cartographic.longitude * radiansPerDegree;
      let angle = -Math.atan2(
        Math.sin(lon1 - lon2) * Math.cos(lat2),
        Math.cos(lat1) * Math.sin(lat2) -
          Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon1 - lon2),
      );
      if (angle < 0) {
        angle += Math.PI * 2.0;
      }

      y.push(Math.sin(angle) * s + y[i]);
      x.push(Math.cos(angle) * s + x[i]);
    }

    let sum = 0;
    for (let i = 0; i < x.length - 1; i += 1) {
      sum += x[i] * y[i + 1] - x[i + 1] * y[i];
    }

    return Math.abs(sum + x[x.length - 1] * y[0] - x[0] * y[y.length - 1]) / 2;
  }

  private _Cartesian2turfPolygon(positions: Cartesian2[]): Polygon {
    const coordinates: number[][][] = [[]];
    positions.forEach((element) => {
      coordinates[0].push([element.x, element.y]);
    });
    coordinates[0].push([positions[0].x, positions[0].y]);
    const pg = polygon(coordinates);
    return pg.geometry;
  }

  private _intersect(poly1: Polygon, poly2: Polygon): Cartesian2[] {
    const intersection = intersect(poly1, poly2);
    if (intersection?.geometry !== undefined) {
      return this._turfPloygon2CartesianArr(intersection?.geometry as Polygon);
    } else {
      return [];
    }
  }

  private _turfPloygon2CartesianArr(polygon: Polygon): Cartesian2[] {
    return polygon.coordinates[0].map(
      (item) => new Cartesian2(item[0], item[1]),
    );
  }

  /**
   * 计算贴地的多边形面积
   * @param {Cartesian3[]} positions 点位
   * @returns {number} 面积/平方米
   */
  getArea(positions: Cartesian3[]): number {
    const area = this._calculateSurfaceArea(
      positions.map(
        (item) =>
          SceneTransforms.worldToWindowCoordinates(this._viewer.scene, item)!,
      ),
    );

    return area;
  }
}

export const getNgAreaSurfaceMeasure = (viewer: Viewer, options: any) => {
  const m = new NgAreaSurfaceMeasure(viewer, {
    ngOnMeasureComplete: options.ngOnMeasureComplete,
    units: 'kilometers',
    locale: {
      start: '',
      area: '',
      total: '',
      formatArea: (area: number, unitedArea: number) => {
        if (area > 1000000) {
          return area + 'km²';
        }
        return unitedArea + 'm²';
      },
    },
  });

  return m;
};
